import { FiShield, FiZap, FiGlobe, FiCpu, FiLock } from "react-icons/fi";
import { IoScanCircleSharp } from "react-icons/io5";
import { SiDotnet, SiCsharp } from "react-icons/si";
import { VscAzure } from "react-icons/vsc";
import { LuSettings, LuUsers, LuShieldCheck, LuPercent, LuShield, LuCircleCheck, LuCpu, LuClipboardList, LuCode, LuBug, LuRocket, LuDatabase, LuClock } from "react-icons/lu";

export const dotnetBenefits = [
  {
    icon: <FiLock className="w-8 h-8" />,
    title: "Enterprise Security",
    desc: "Built-in protection against common vulnerabilities and advanced threat detection.",
  },
  {
    icon: <FiCpu className="w-8 h-8" />,
    title: "High Performance",
    desc: ".NET Core is one of the fastest web frameworks in the world according to independent benchmarks.",
  },
  {
    icon: <FiGlobe className="w-8 h-8" />,
    title: "Cross-Platform",
    desc: "Develop once and deploy across Windows, Linux, and macOS with consistent performance.",
  },
];

export const dotnetServices = [
  {
    icon: <SiDotnet size={36} />,
    title: "ASP.NET Core Web",
    description: "Building modern, cloud-based apps that are scalable and performant for large enterprises.",
    image: "/images/fullstack-dev.jpg",
    link: "#",
  },
  {
    icon: <IoScanCircleSharp size={36} />,
    title: "C# Custom Logic",
    description: "Developing complex business logic and algorithms with the power of type-safe C#.",
    image: "/images/custom-dev.jpg",
    link: "#",
  },
  {
    icon: <VscAzure size={36} />,
    title: "Azure Cloud Solutions",
    description: "Architecture and deployment of .NET applications on the Microsoft Azure global cloud.",
    image: "/images/webapp-dev.jpg",
    link: "#",
  },
];

export const dotnetFaqs = [
  {
    q: "Why should I choose .NET development in India for my business?",
    a: ".NET development in India offers cost-effective solutions with skilled developers who can build secure, scalable, and high-performance web and desktop applications. .NET is a powerful framework for creating enterprise-level solutions that meet modern business needs.",
  },
  {
    q: "What can Dot Net developers in India do for my business?",
    a: "Dot Net developers in India design and develop web, desktop, and enterprise applications using Microsoft’s .NET framework. They handle backend logic, database integration, API connections, and ensure your application is secure, scalable, and efficient.",
  },
  {
    q: "Why should I hire Dot Net developers for my project?",
    a: "Hiring Dot Net developers ensures your project is built on a reliable and versatile framework. They can create custom applications, integrate complex systems, and deliver high-quality solutions that are maintainable and scalable for long-term business growth.",
  },
];

export const dotnetProcessSteps = [
  {
    step: "01",
    title: "Enterprise Architecture",
    description: "Designing the N-tier architecture and determining Entity Framework strategies.",
    points: ["N-tier planning", "EF Core strategy", "AuthN/AuthZ design"],
  },
  {
    step: "02",
    title: "Development Sprints",
    description: "Building features using modern C# patterns and the powerful .NET ecosystem.",
    points: ["C# development", "NuGet orchestration", "Unit testing (xUnit)"],
  },
  {
    step: "03",
    title: "Security & QA",
    description: "Rigorous security audits and performance profiling for enterprise-grade stability.",
    points: ["Statical analysis", "Load profiling", "Azure deployment"],
  },
];

export const dotnetTechStack = [".NET Core", "C#", "EF Core", "SQL Server", "Azure", "AutoMapper", "MediatR"];

export const dotnetHeroData = {
  backgroundImage: "/images/serviceBanners/netBanner.webp",
  whiteImage: "/images/serviceBanners/netBannerW.webp",
  subTitle: ".NET Development Services",
  title: (
    <>
      Enterprise-Grade <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">.NET Applications</span>
    </>
  ),
  description:
    "We build secure, scalable, and high performance .NET applications using C# and ASP.NET Core to deliver enterprise grade backend systems that ensure reliability, efficiency, and robust digital business solutions.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("Get Started clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "C# Performance",
      desc: "High-performance compiled and type-safe execution for reliable applications.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Cross-Platform Deployment",
      desc: "Run applications smoothly on Windows, Linux, and cloud environments.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Identity & Access Control",
      desc: "Built-in authentication and role-based security management.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "EF Core Mapping",
      desc: "Efficient database mapping with optimized data access layers.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp", // Re-use generic icon or update with dot-net asset path
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

export const dotnetStatsData = [
  {
    icon: LuSettings,
    value: "140+",
    label: ".NET Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "60+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.99%",
    label: "Server Uptime",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuCpu,
    value: "12M+",
    label: "Secure Requests",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Queries",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Secure Code",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

export const dotnetSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: ".NET Enterprise Solutions We Build",
  buildCards: [
    {
      title: "ASP.NET Core Web Applications",
      desc: "Build modular web apps and scalable API backends using Razor Pages and MVC architecture.",
      icon: LuCode,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Entity Framework Core",
      desc: "Create efficient ORM-based data layers with code-first migrations and optimized database access.",
      icon: LuDatabase,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Azure Cloud Applications",
      desc: "Develop cloud-native apps using Azure Functions, Blob Storage, and SQL integration.",
      icon: LuRocket,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "CQRS Architecture",
      desc: "Separate command and query logic using MediatR for clean and scalable system design.",
      icon: LuSettings,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "C# Microservices",
      desc: "Build containerized microservices using Kubernetes and gRPC for high scalability.",
      icon: LuCpu,
      colorClass: "text-[#6366f1]",
      bgClass: "bg-[#6366f1]/10",
      borderClass: "border-[#6366f1]/20 hover:border-[#6366f1]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(99,102,241,0.25)]",
    },
    {
      title: "Legacy System Migration",
      desc: "Upgrade legacy ASP.NET applications to modern, cross-platform .NET Core solutions.",
      icon: LuClock,
      colorClass: "text-[#d946ef]",
      bgClass: "bg-[#d946ef]/10",
      borderClass: "border-[#d946ef]/20 hover:border-[#d946ef]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(217,70,239,0.25)]",
    },
  ],
  processSubtitle: ".NET ENGINEERING PROCESS",
  processTitle: ".NET Engineering Process",
  processSteps: [
    {
      step: "01",
      title: "Architecture Planning",
      description: "Design database structure, entity relationships, and scalable layered architecture.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Domain Setup",
      description: "Define domain models, validations, and configuration rules for business logic.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Logic Development",
      description: "Implement controllers, repositories, and core backend business logic.",
      icon: LuSettings,
    },
    {
      step: "04",
      title: "Security Implementation",
      description: "Add JWT authentication, CORS policies, and role-based access control.",
      icon: LuShieldCheck,
    },
    {
      step: "05",
      title: "Testing (xUnit)",
      description: "Write unit and integration tests to ensure stable and completely reliable code.",
      icon: LuCircleCheck, // Replaced LuBug/LuCheckCircle with build-compliant LuCircleCheck
    },
    {
      step: "06",
      title: "Azure Deployment",
      description: "Set up CI/CD pipelines and deploy applications on Azure cloud infrastructure.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE .NET?",
  benefitsTitle: "Why .NET is the Right Choice",
  benefits: [
    "Enterprise-grade stability",
    "High C# execution speed",
    "Built-in security standards",
    "Cross-platform consistency",
    "Visual Studio ecosystem support",
    "Cloud scalability support",
    "Microsoft backed framework",
  ],
  logoImage: "/images/serviceBanners/netBanner.webp",
  whiteLogoImage: "/images/serviceBanners/netBannerW.webp",
};

export const dotnetPortfolioData = {
  subtitle: "OUR WORK",
  title: ".NET Solutions We're Proud Of",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "Invoicer Enterprise",
      badge: "ASP.NET Core API",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Robust invoice management engine handling multi-tenant databases with strict isolation.",
      downloads: "15M+ Rec",
      rating: "5.0",
      image: "/images/WebAppDevelopment/NET_Development.png",
    },
    {
      title: "MedSync Portal",
      badge: "C# Microservices",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "HIPAA compliant backend orchestrating health records with Azure Storage.",
      downloads: "2M+ Patients",
      rating: "4.8",
      image: "/images/WebAppDevelopment/NET_Development.png",
    },
  ],
};

export const dotnetTechnologiesData = {
  techTitle: "TECHNOLOGIES WE USE",
  technologies: [
    { name: "REST API", icon: "RestAPI" },
    { name: "SQLite", icon: "SQLite" },
    { name: "Firebase", icon: "Firebase" },
    { name: "Provider", icon: "Provider" },
  ],
  trustTitle: "TRUSTED BY BUSINESSES WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

export const dotnetAboutData = [
  {
    title: "Dot Net Development Services for Enterprise-Grade Applications",
    content: (
      <>
        Modern businesses need backend systems that remain secure, scalable, and stable as operations expand. Reliable{" "}
        <b className="text-text-primary font-semibold">dot net development services</b> help organizations build high-performance applications that support complex workflows and
        long-term business growth. Many companies struggle with legacy systems and backend environments that limit performance and flexibility. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, .NET solutions are developed around business objectives, operational requirements, and long-term
        scalability to help organizations build dependable enterprise-grade digital systems.
      </>
    ),
  },
  {
    title: "ASP NET Development Company for Reliable Business Systems",
    content: (
      <>
        A successful enterprise application depends on structured architecture and dependable performance. A trusted{" "}
        <b className="text-text-primary font-semibold">asp net development company</b> helps businesses create scalable platforms that improve workflow efficiency and support
        growing operational needs. Companies investing in <b className="text-text-primary font-semibold">dot net web development</b> often benefit from stronger system reliability
        because applications are designed to support complex business environments with long-term maintainability. Tycho Technologies focuses on practical .NET frameworks that
        improve backend efficiency while supporting digital transformation and business continuity.
      </>
    ),
  },
  {
    title: "Hire Dot Net Developers for Smarter Infrastructure",
    content: (
      <>
        Businesses handling enterprise operations require backend environments that remain adaptable and reliable over time. Companies looking to{" "}
        <b className="text-text-primary font-semibold">hire dot net developers</b> often prioritize scalability, maintainability, and stronger system performance for sustainable
        growth. Organizations aiming to modernize digital ecosystems frequently strengthen backend flexibility through{" "}
        <a href="https://tychotechnologies.com/symfony-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          symfony development services
        </a>{" "}
        to support connected and scalable business operations.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Dot Net Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">dot net development company in india</b> continues to grow as businesses seek secure and
        future-ready enterprise systems. Companies investing in <b className="text-text-primary font-semibold">dot net development services india</b> often focus on operational
        stability, long-term maintainability, and stronger application performance. Businesses partnering with Tycho Technologies gain access to customized .NET strategies designed
        around measurable business goals and technical requirements. Many organizations also improve backend agility through{" "}
        <a href="https://tychotechnologies.com/nodejs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          node js development services
        </a>{" "}
        to support modern and scalable digital infrastructures.
      </>
    ),
  },
];
